/**
 * MpwaCmp — Consent Management Platform SDK.
 *
 *   <script src="https://cdn.mpwa.to/cmp.min.js" async></script>
 *   <script>MpwaCmp.init({ siteId: 42 });</script>
 *
 * On init:
 *   1. Load any persisted decision and replay it through the TCF/GPP APIs.
 *   2. If no decision (or `forceShow: true`), fetch the public config and
 *      render the banner.
 *   3. Once the user picks an option, build a TC string + GPP string, persist,
 *      POST the submission to the publisher service, and fire `onApplied`.
 */

import { renderBanner, flattenVendorIds, type BannerLocale } from './banner.js';
import { loadDecision, saveDecision, clearDecision } from './storage.js';
import { buildGppString, buildTcString, detectRegion, resolvePurposesDefaultGrants } from './tcstring.js';
import type { CmpConfig, ConsentDecision, GppApiCommand, InitOptions, TcfApiCommand } from './types.js';

/**
 * Region → default banner locale. Used when neither the call site nor the
 * publisher config specified one explicitly. Exported so tests can pin the
 * mapping without re-implementing the heuristic.
 */
export function localeForRegion(region: string | null): BannerLocale | null {
  if (region === 'BR') return 'pt-BR';
  if (region === 'CN') return 'zh-CN';
  if (region === 'SA' || region === 'AE' || region === 'EG' || region === 'MA') return 'ar';
  return null;
}

const DEFAULT_BASE_URL = 'https://api.mpwa.to';

let currentDecision: ConsentDecision | null = null;
let currentConfig: CmpConfig | null = null;
let removeBanner: (() => void) | null = null;
let listeners: Array<(d: ConsentDecision | null) => void> = [];

export async function init(opts: InitOptions): Promise<void> {
  installTcfApiStub();
  installGppApiStub();

  const baseUrl = (opts.baseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, '');
  const siteId = String(opts.siteId);

  // Replay any persisted decision immediately so downstream tags can read
  // the TCF API stub without waiting for the network. The banner re-renders
  // below if no decision exists or `forceShow` is set.
  const stored = loadDecision();
  if (stored && !opts.forceShow) {
    currentDecision = stored;
    notify();
    if (opts.onApplied) opts.onApplied(stored);
    return;
  }

  let cfg: CmpConfig;
  try {
    cfg = await fetchJson<CmpConfig>(`${baseUrl}/v1/cmp/config/${encodeURIComponent(siteId)}`);
  } catch (err) {
    console.warn('[MpwaCmp] failed to load config, deferring banner', err);
    return;
  }
  currentConfig = cfg;
  if (!cfg.enabled) return;

  const region = opts.region ?? detectRegion();
  // Region-aware locale auto-pick: a Brazilian visitor lands on pt-BR by
  // default even when the publisher's localeDefault is 'en'; same for CN.
  // Explicit `opts.locale` always wins, then the publisher default, then
  // region inference, then EN.
  const locale = opts.locale ?? cfg.localeDefault ?? localeForRegion(region) ?? 'en';

  // If we're in a strict region and the user has no decision, we must show.
  // If we're in an opt-out region we still show the banner so the user can
  // change defaults; the difference is that we treat "no answer" as opt-in.
  const inStrict = region != null && cfg.regionStrict.some((r) => region.startsWith(r));
  const inOptout = region != null && cfg.regionOptout.some((r) => region.startsWith(r));

  removeBanner = renderBanner(cfg, locale, {
    onAcceptAll: () => applyDecision({
      decision: 'accept_all',
      purposesGranted: cfg.purposes.map((p) => p.id),
      purposesDenied: [],
      vendorGrants: flattenVendorIds(cfg.vendors),
      cfg,
      region,
      siteId,
      baseUrl,
      onApplied: opts.onApplied,
    }),
    onRejectAll: () => applyDecision({
      decision: 'reject_all',
      purposesGranted: cfg.purposes.filter((p) => p.required).map((p) => p.id),
      purposesDenied: cfg.purposes.filter((p) => !p.required).map((p) => p.id),
      vendorGrants: [],
      cfg,
      region,
      siteId,
      baseUrl,
      onApplied: opts.onApplied,
    }),
    onCustom: (granted) => {
      const grantedSet = new Set([...granted, ...cfg.purposes.filter((p) => p.required).map((p) => p.id)]);
      const denied = cfg.purposes.filter((p) => !grantedSet.has(p.id)).map((p) => p.id);
      applyDecision({
        decision: 'custom',
        purposesGranted: Array.from(grantedSet),
        purposesDenied: denied,
        vendorGrants: flattenVendorIds(cfg.vendors),
        cfg,
        region,
        siteId,
        baseUrl,
        onApplied: opts.onApplied,
      });
    },
  });

  // Soft pre-populate the persisted defaults when in an opt-out region: the
  // user is treated as opted in unless they reject. We still wait for explicit
  // confirmation in strict regions.
  if (!inStrict && inOptout && !currentDecision) {
    const granted = resolvePurposesDefaultGrants(cfg);
    applyDecision({
      decision: 'custom',
      purposesGranted: granted,
      purposesDenied: cfg.purposes.filter((p) => !granted.includes(p.id)).map((p) => p.id),
      vendorGrants: flattenVendorIds(cfg.vendors),
      cfg,
      region,
      siteId,
      baseUrl,
      // suppressClose=true so the banner stays visible — the user can still
      // change their mind, but ads can render now.
      suppressClose: true,
      onApplied: opts.onApplied,
    });
  }
}

interface ApplyArgs {
  decision: 'accept_all' | 'reject_all' | 'custom';
  purposesGranted: number[];
  purposesDenied: number[];
  vendorGrants: number[];
  cfg: CmpConfig;
  region: string | null;
  siteId: string;
  baseUrl: string;
  suppressClose?: boolean | undefined;
  onApplied?: ((d: ConsentDecision) => void) | undefined;
}

function applyDecision(a: ApplyArgs): void {
  // TC string carries an ISO 639-1 language code (2 letters). Strip the
  // BCP-47 region suffix for pt-BR / zh-CN so the encoder sees 'pt' / 'zh'.
  const localeRaw = a.cfg.localeDefault ?? 'en';
  const tcLanguage: 'en' | 'ar' = localeRaw === 'ar'
    ? 'ar'
    : 'en'; // pt-BR + zh-CN fall through to 'en' in the TC core string;
            // the region-specific GPP sub-string carries the real signal.
  const tcString = buildTcString({
    purposesGranted: a.purposesGranted,
    vendorGrants: a.vendorGrants,
    language: tcLanguage,
    publisherCC: a.region?.startsWith('US') ? 'US' : (a.region ?? 'EG'),
  });
  const { gppString, sectionIds } = buildGppString({
    doNotSell: a.decision === 'reject_all',
    region: a.region,
  });
  const decision = saveDecision({
    decision: a.decision,
    purposesGranted: a.purposesGranted,
    purposesDenied: a.purposesDenied,
    vendorGrants: a.vendorGrants,
    tcString,
    gppString,
    gppSid: sectionIds,
    region: a.region,
  });
  currentDecision = decision;
  notify();

  // Best-effort upload — failure is logged but never blocks the page.
  const payload = {
    siteId: a.siteId,
    decision: a.decision,
    tcString,
    gppString,
    gppSid: sectionIds,
    region: a.region,
    purposesGranted: a.purposesGranted,
    purposesDenied: a.purposesDenied,
    vendorGrants: a.vendorGrants,
  };
  fetch(`${a.baseUrl}/v1/cmp/consent`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
    credentials: 'omit',
  }).catch((err) => console.warn('[MpwaCmp] consent upload failed', err));

  if (!a.suppressClose && removeBanner) {
    removeBanner();
    removeBanner = null;
  }
  if (a.onApplied) a.onApplied(decision);
}

export function getDecision(): ConsentDecision | null {
  return currentDecision;
}

export function reset(): void {
  clearDecision();
  currentDecision = null;
  notify();
}

export function subscribe(fn: (d: ConsentDecision | null) => void): () => void {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
}

function notify(): void {
  for (const fn of listeners) {
    try {
      fn(currentDecision);
    } catch (err) {
      console.warn('[MpwaCmp] listener threw', err);
    }
  }
}

// ---------------------------------------------------------------------------
// IAB TCF and GPP API stubs. We expose enough of the surface that downstream
// vendors (e.g. Google Ad Manager, Prebid wrappers) can detect a CMP and pull
// the TC string. The stubs are intentionally synchronous against the cached
// `currentDecision` — they never call out to the publisher service.
// ---------------------------------------------------------------------------

function installTcfApiStub(): void {
  if (typeof window === 'undefined') return;
  if ((window as unknown as { __tcfapi?: unknown }).__tcfapi) return;
  const w = window as unknown as {
    __tcfapi: (command: string, version: number, callback: TcfApiCommand['callback'], parameter?: unknown) => void;
  };
  w.__tcfapi = (command, _version, callback, _parameter) => {
    if (typeof callback !== 'function') return;
    switch (command) {
      case 'ping':
        return callback({
          cmpLoaded: true,
          cmpStatus: 'loaded',
          displayStatus: currentDecision ? 'hidden' : 'visible',
          apiVersion: '2.2',
          gdprApplies: true,
        }, true);
      case 'getTCData':
        if (!currentDecision) return callback(null, false);
        return callback({
          tcString: currentDecision.tcString,
          tcfPolicyVersion: 4,
          gdprApplies: true,
          eventStatus: 'tcloaded',
          cmpStatus: 'loaded',
          cmpId: 999,
          cmpVersion: 1,
          purpose: { consents: bitmap(currentDecision.purposesGranted) },
          vendor: { consents: bitmap(currentDecision.vendorGrants) },
        }, true);
      case 'addEventListener': {
        // Fire immediately and again on changes.
        const fire = (d: ConsentDecision | null) =>
          callback({
            eventStatus: d ? 'useractioncomplete' : 'cmpuishown',
            cmpStatus: 'loaded',
            tcString: d?.tcString ?? '',
            gdprApplies: true,
          }, true);
        fire(currentDecision);
        subscribe(fire);
        return;
      }
      case 'removeEventListener':
        return callback(true, true);
      default:
        return callback(null, false);
    }
  };
}

function installGppApiStub(): void {
  if (typeof window === 'undefined') return;
  if ((window as unknown as { __gpp?: unknown }).__gpp) return;
  const w = window as unknown as {
    __gpp: (command: string, callback?: GppApiCommand['callback'], parameter?: unknown) => void;
  };
  w.__gpp = (command, callback, _parameter) => {
    if (typeof callback !== 'function') return;
    switch (command) {
      case 'ping':
        return callback({
          gppVersion: '1.0',
          cmpStatus: 'loaded',
          cmpDisplayStatus: currentDecision ? 'hidden' : 'visible',
          applicableSections: currentDecision?.gppSid ?? [7],
          gppString: currentDecision?.gppString ?? '',
        }, true);
      case 'getGPPData':
        if (!currentDecision) return callback(null, false);
        return callback({
          gppString: currentDecision.gppString,
          applicableSections: currentDecision.gppSid,
          supportedAPIs: ['tcfeuv2', 'usnat'],
        }, true);
      case 'addEventListener': {
        const fire = (d: ConsentDecision | null) =>
          callback({
            eventName: d ? 'cmpStatus' : 'cmpDisplay',
            data: d?.gppString ?? '',
            applicableSections: d?.gppSid ?? [],
          }, true);
        fire(currentDecision);
        subscribe(fire);
        return;
      }
      default:
        return callback(null, false);
    }
  };
}

function bitmap(ids: number[]): string {
  if (!ids.length) return '';
  const max = Math.max(...ids);
  let s = '';
  const set = new Set(ids);
  for (let i = 1; i <= max; i++) s += set.has(i) ? '1' : '0';
  return s;
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'omit' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

// Auto-init from a data-attribute on the script tag if no manual init runs.
if (typeof document !== 'undefined') {
  setTimeout(() => {
    const tag = document.querySelector<HTMLScriptElement>('script[data-mpwa-cmp-site]');
    if (tag && !currentConfig && !currentDecision) {
      const sid = tag.getAttribute('data-mpwa-cmp-site');
      const base = tag.getAttribute('data-mpwa-cmp-base');
      const locale = tag.getAttribute('data-mpwa-cmp-locale') as 'en' | 'ar' | null;
      if (sid) {
        const opts: InitOptions = { siteId: sid };
        if (base) opts.baseUrl = base;
        if (locale) opts.locale = locale;
        init(opts).catch(() => {});
      }
    }
  }, 0);
}
